<script lang="ts" setup>
    import { computed, ref, watch, inject, onMounted, useTemplateRef } from "vue";
    import { v4 as uuidv4 } from "uuid";
    import {
        watchDeep,
        useFocusWithin,
        useMagicKeys,
        useActiveElement,
        useIntersectionObserver
    } from "@vueuse/core";
    import Pagination from "@/components/Pagination.vue";
    // import { route } from "ziggy-js";
    import { router } from "@inertiajs/vue3";
    import { useRouter } from "@/helpers/composables/useRouter";
    import { useAttrs } from 'vue';
    interface ContentItemField {
        label: string;
        field: string;
        is_custom?: boolean;
        sortable?: boolean;
        pt?: Object;
        editable: boolean
    }
    interface ContentItem {
        visible_fields?: ContentItemField[];
        data: any;
    }
    let props = defineProps({
        visibleFields: {
            type: Array as PropType<ContentItemField[]>,
            required: false,
            default: () => []
        },
        checkable: {
            type: Boolean,
            required: false,
            default: false
        },
        checkableOnMobile: {
            type: Boolean,
            required: false,
            default: false
        },
        rowIsCheckable: {
            type: Function,
            required: false,
            default: () => true
        },
        sortable: {
            type: Boolean,
            required: false,
            default: false
        },
        zebra: {
            type: Boolean,
            required: false,
            default: true
        },
        useBackendSorting: {
            type: Boolean,
            required: false,
            default: false
        },
        checkAll: {
            type: Boolean,
            required: false,
            default: true
        },
        primaryKey: {
            type: String,
            required: false,
            default: "id"
        },
        backendPagination: {
            type: Object,
            required: false,
            default: () => ({
                total: 0,
                current_page: 1,
                per_page: 10,
                last_page: 1
            })
        },
        focusable: {
            type: Boolean,
            required: false,
            default: false
        },
        pt: {
            type: Object,
            default(){
                return {}
            }
        },
        dataKey: {
            type: String,
            default: ''
        },
        expandedRows: {
            type: Array,
            default(){
                return []
            }
        },
        editMode: {
            type: String,
            default: ''
        },
        virtualScrollerOptions: {
            type: Object,
            default(){
                return {}
            }
        },
        hasPadding: {
            type: Boolean,
            default: false
        },
        useVirtualRows: {
            type: Boolean,
            default: false
        },
        emptyRowsMessage: {
            type: String,
            default: "No data available"
        },
    });

    const attrs = useAttrs();

    let route = inject("route");

    const existingRouteParams = computed(()=>{
        let routeParams = JSON.parse(JSON.stringify(route()?.params ?? {}));
        delete routeParams['page'];
        return routeParams;
    })

    let computedBackendPagination = computed(() => {
        return {
            totalItems: props.backendPagination.total,
            currentPage: props.backendPagination.current_page,
            pageSize: props.backendPagination.per_page,
            maxPages: props.backendPagination.last_page
        };
    });

    let maxPages = computed(() => computedBackendPagination.value.maxPages);

    let emits = defineEmits(["sort", "cellEditComplete"]);

    let content = defineModel("content", {
        type: Array as PropType<ContentItem[]>,
        required: true,
        default: () => []
    });

    const maxItemsToShow = ref(props.content.length < 20 ? props.content.length : 20);
    const contentComputed = computed(()=>{
        return props.useVirtualRows ? content.value?.slice(0, maxItemsToShow.value) : content.value;
    })
    const reachedBottom = computed(()=>{
        return props.useVirtualRows ? maxItemsToShow.value == props.content.length : true;
    })

    let headers = computed(() => {
        if (props.visibleFields.length > 0 || content.value.length > 0) {
            return (
                props.visibleFields.length > 0
                    ? props.visibleFields
                    : Array.isArray(content.value[0].visible_fields)
                      ? content.value[0].visible_fields
                      : []
            ).filter(field => {
                return 'visible' in field ? field.visible : true;
            }).map((field) => {
                return {
                    label: field.label,
                    key: field.field,
                    is_custom: field.is_custom ?? false,
                    show_only: field.show_only ?? null,
                    sortable: field.sortable ?? false,
                    pt: field?.pt ?? {},
                    editable: field.editable ?? false
                };
            });
        }
        return [];
    });

    function getCellContent(row, cell) {
        return row.data[cell.key];
    }

    function getShowOnlyClass(str) {
        return str ? (str === 'mobile' ? 'mobile-only' : 'desktop-only') : null
    }

    function getCellPt(column){
        let pt = Object.assign({}, column.pt);
        let showOnlyClass = getShowOnlyClass(column.show_only);
        if(showOnlyClass){
            if(typeof pt['headerCell'] !== 'function'){
                pt['headerCell'] = {...pt['headerCell'], class: [pt?.['headerCell']?.class, showOnlyClass].join(' ')};
            }
            if(typeof pt['bodyCell'] !== 'function'){
                pt['bodyCell'] = {...pt['bodyCell'], class: [pt?.['bodyCell']?.class, showOnlyClass].join(' ')};
            }
        }
        return pt;
    }

    let { to } = useRouter({
        router,
        route
    });

    let checkedRows = defineModel("checkedRows", {
        type: Array,
        default: () => []
    });

    function checkRow(row: number, event: Event) {
        let check = event.target.checked;
        if (check) {
            checkedRows.value = [...checkedRows.value, content.value[row]];
        } else {
            let data = content.value[row].data;
            checkedRows.value = checkedRows.value.filter(
                (r, index) => r.data[props.primaryKey] != data[props.primaryKey]
            );
        }
    }

    const checkAllVal = computed({
        get(){
            return content.value.length > 0 && Object.keys(isChecked.value).every(rowId=>isChecked.value[rowId]);
        },
        set(newVal){
            if (newVal) {
                checkedRows.value = [...content.value].filter((row) => {
                    return props.rowIsCheckable(row.data);
                });
            } else {
                checkedRows.value = [];
            }
        }
    })

    const isChecked = computed(()=>{
        return content.value.reduce((acc,row)=>{
            acc[row.data[props.primaryKey]] = checkedRows.value.findIndex((r) => r.data[props.primaryKey] == row.data[props.primaryKey]) > -1;
            return acc;
        }, {})
    })

    let sortableHeaders = computed(() => {
        return headers.value.filter((header) => header.sortable);
    });

    let defaultSort = route()?.params?.orderBy ?? null;
    let sort = ref({
        key: defaultSort?.split(":")?.[0] ?? "",
        direction: defaultSort?.split(":")?.[1] ?? "asc",
    });
    if (defaultSort) {
        let [key, direction] = defaultSort.split(":");
        sort.value = {
            key,
            direction
        };
    }

    let tableContentKey = ref(uuidv4());

    function sortBy({ sortField, sortOrder}) {
        if (!sortableHeaders.value.find((header) => header.key == sortField)) {
            return;
        }
        let sortDirection = sortOrder == 1 ? "asc" : "desc";
        if (!props.useBackendSorting) {
            let sorted = [...content.value].sort(
                (a: ContentItem, b: ContentItem) => {
                    if (a.data[sortField] > b.data[sortField]) {
                        return sortDirection == "asc" ? 1 : -1;
                    } else if (a.data[sortField] < b.data[sortField]) {
                        return sortDirection == "asc" ? -1 : 1;
                    }
                    return 0;
                }
            );
            content.value = sorted;
            tableContentKey.value = uuidv4();
        }

        // sort.value.direction = sort.value.direction == "asc" ? "desc" : "asc";
        // sort.value.key = field;

        if (props.useBackendSorting) {
            try {
                to({
                    newParams: {
                        ...existingRouteParams.value,
                        orderBy: `${sortField}:${sortDirection}`
                    },
                    options: {preserveState: true, preserveScroll: true}
                });
            } catch (err) {
                console.error("here", err);
            }
            // call backend to sort
            emits("sort", {
                key: sortField,
                direction: sortDirection
            });
        }
    }

    let isEmpty = computed(() => content.value.length == 0);

    let container = ref();
    let isFocused = defineModel("focused", {
        type: Boolean,
        default: false
    });

    let selectedRow = defineModel("selectedRow", {
        type: Object,
        default: null
    });

    watch(content, (newVal) => {
        maxItemsToShow.value = newVal.length;
    })
    if (props.focusable) {
        const activeElement = useActiveElement();

        let { focused } = useFocusWithin(container);
        // let focused = ref(false);

        let { arrowup, arrowdown, enter } = useMagicKeys();
        watch(focused, (newVal) => {
            isFocused.value = newVal;
        });

        watch(arrowup, (newVal) => {
            if (newVal && isFocused.value) {
                let rows = container.value.$el.querySelectorAll("tr");
                let row = container.value.$el.querySelector("tr:focus-within");
                let index = Array.from(rows).indexOf(row);
                // if the element that is in focus is a number input, then we don't want to move focus
                let isActiveNumberInput =
                    activeElement.value?.tagName === "INPUT" &&
                    activeElement.value?.type === "number";
                if (isActiveNumberInput) {
                    return;
                }

                if (index > 0) {
                    rows[index - 1].focus();
                } else {
                    rows[rows.length - 1].focus();
                }
            }
        });

        watch(arrowdown, (newVal) => {
            if (newVal && isFocused.value) {
                // move focus down to next row in container
                let rows = container.value.$el.querySelectorAll("tr");
                let row = container.value.$el.querySelector("tr:focus-within");
                let index = Array.from(rows).indexOf(row);
                let isActiveNumberInput =
                    activeElement.value?.tagName === "INPUT" &&
                    activeElement.value?.type === "number";
                if (isActiveNumberInput) {
                    return;
                }
                if (index < rows.length - 1) {
                    rows[index + 1].focus();
                } else {
                    rows[0].focus();
                }
            }
        });

        watch(enter, (newVal) => {
            if (newVal && isFocused.value) {
                let row = container.value.$el.querySelector("tr:focus-within");
                let index = Array.from(
                    container.value.$el.querySelectorAll("tr")
                ).indexOf(row);
                selectedRow.value = content.value[index - 1];
            }
        });

        onMounted(()=>{
            let rows = container.value.$el.querySelectorAll?.(".table-item");
            rows[0]?.focus?.();

            if(props.useVirtualRows){
                const target = useTemplateRef<HTMLDivElement>('tableBottom');
                const { stop } = useIntersectionObserver(
                    target,
                    ([entry], observerElement) => {
                        if(maxItemsToShow.value < props.content.length){
                            maxItemsToShow.value = Math.min((maxItemsToShow.value + 20), props.content.length);
                        }
                        if(reachedBottom.value){
                            stop();
                        }
                    },
                    {
                        rootMargin: "300px"
                    }
                );
            }
            
        })
    }else{
        onMounted(()=>{
            if(props.useVirtualRows){
                const target = useTemplateRef<HTMLDivElement>('tableBottom');
                const { stop } = useIntersectionObserver(
                    target,
                    ([entry], observerElement) => {
                        if(maxItemsToShow.value < props.content.length){
                            maxItemsToShow.value = Math.min((maxItemsToShow.value + 20), props.content.length);
                        }
                        if(reachedBottom.value){
                            stop();
                        }
                    },
                    {
                        rootMargin: "300px"
                    }
                );
            }
        })
    }

    function setRowClick(event){
        selectedRow.value = content.value[event.index];
    }
</script>

<template>
    <div>
    <PrimeDataTable @sort="sortBy" :value="contentComputed" :sortField="sort?.key" :sortOrder="sort?.direction == 'asc' ? 1 : -1"
        :pt="{
            tbody: {
                key: tableContentKey
            },
            ...props?.pt
        }"
        :dataKey="dataKey"
        :expandedRows="expandedRows"
        :editMode="$props.editMode"
        @cell-edit-complete="$event=>emits('cellEditComplete', $event)"
        ref="container"
        stripedRows
        :virtualScrollerOptions="$props.virtualScrollerOptions"
        @row-click="setRowClick"
        :class="{'table-has-padding': props.hasPadding}"
        v-bind="attrs"
    >
        <PrimeColumn v-if="checkable" :class="['text-center', { 'desktop-only': !checkableOnMobile }]">
            <template #header>
                <div style="display:flex; flex-flow:row; justify-content: center;">
                    <input :disabled="content?.length == 0" v-if="checkAll" type="checkbox" id="check_all" name="sort" aria-label="check all" v-model="checkAllVal">
                </div>
            </template>
            <template #body="{ data, index }">
                <div style="display:flex; flex-flow:row; justify-content: center;">
                    <input :key="data.data[primaryKey]" type="checkbox" :id="`check_${primaryKey}_${[data.data[primaryKey]]}`" name="sort" aria-label="check all" @input.prevent="(event) => checkRow(index, event)" :disabled="!rowIsCheckable(data.data)" :checked="isChecked[data.data[primaryKey]]">
                </div>
            </template>
        </PrimeColumn>
        <PrimeColumn v-for="(column) in headers" :key="column.key" :field="column.key" :pt="getCellPt(column)" :sortable="column.sortable">
            <template #header="{}">
                {{ column.label }}
                <!-- TODO: utilize primevue datatable sorting -->
            </template>
            <template #body="{ data, index }">
                <p v-if="!column.is_custom" class="paragraph-default" v-bind="column.pt.bodyCellContent">
                    {{ data.data[column.key] }}
                </p>
                <slot v-else :name="column.key + '_' + index" v-bind="{ data, header: column, content }"></slot>
            </template>
            <template #editor="{ data, index }">
                <slot v-if="column.editable" :name="column.key + '_editor_' + index" v-bind="{ data, header: column, content }"></slot>
                <slot v-else-if="column.is_custom" :name="column.key + '_' + index" v-bind="{ data, header: column, content }"></slot>
                <p v-else class="paragraph-default" v-bind="column.pt.bodyCellContent">
                    {{ data.data[column.key] }}
                </p>
            </template>
        </PrimeColumn>

        <template #expansion="{ data, index }">
            <slot name="expansion" v-bind="{ data, index, content }"></slot>
        </template>

        <template #empty>
            {{props.emptyRowsMessage}}
        </template>

        <template #footer>
            <div v-show="!reachedBottom" ref="tableBottom" id="table-bottom" class="skeleton" style="height:100px; box-sizing: border-box;"></div>
        </template>
    </PrimeDataTable>
    <pagination class="pagination"
        v-if="(!isEmpty && maxPages > 1)"
        :pagination="computedBackendPagination"
        @pageChange="(page) => to({ newParams: {...existingRouteParams, page}, options: {preserveState: true} })"
    />
    </div>
</template>
